/**
 * PATH: apps/ai/src/pages/ReportsPage.tsx
 * PURPOSE: Orchestrator — AI-powered report generation page
 */

import { useState } from 'react';
import { FileText, Clock, Loader2, Sparkles, Settings } from 'lucide-react';
import { cn } from '../lib/utils';
import { getAuthCookie } from '@quotech/auth';
import { API_BASE } from '@quotech/api';
import { buildOrganisationHeader } from '../../organisation';
import { REPORT_TEMPLATES, type GeneratedReport } from './reports/types';
import { TemplateCard, ParameterForm, GenerationProgress } from './reports/ReportComponents';

export default function ReportsPage() {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);
  const [recentReports, setRecentReports] = useState<GeneratedReport[]>([]);

  const template = selectedTemplate ? REPORT_TEMPLATES.find(t => t.id === selectedTemplate) : null;

  const handleGenerate = async () => {
    if (!template) return;
    setGenerating(true);
    const token = getAuthCookie();
    if (!token) { console.error('No auth token'); setGenerating(false); return; }

    const newReport: GeneratedReport = { id: `pending-${Date.now()}`, templateId: template.id, name: `${template.name} - ${new Date().toLocaleDateString()}`, status: 'generating', progress: 0, createdAt: new Date() };
    setRecentReports(prev => [newReport, ...prev]);

    try {
      const response = await fetch(`${API_BASE}/api/ai/reports/generate/`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...buildOrganisationHeader() }, body: JSON.stringify({ template_id: template.id, parameters: paramValues, save_to_qdrive: true }) });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      const reportId = data.report_id;
      setRecentReports(prev => prev.map(r => r.id === newReport.id ? { ...r, id: reportId } : r));

      const pollStatus = async () => {
        const statusResp = await fetch(`${API_BASE}/api/ai/reports/${reportId}/`, { headers: { 'Authorization': `Bearer ${token}`, ...buildOrganisationHeader() } });
        if (!statusResp.ok) throw new Error('Failed to get report status');
        const statusData = await statusResp.json();
        setRecentReports(prev => prev.map(r => r.id === reportId ? { ...r, status: statusData.status, progress: statusData.progress || 0, downloadUrl: statusData.download_url ? `${API_BASE}${statusData.download_url}` : undefined, qdriveUrl: statusData.qdrive_url, data: statusData.data } : r));
        if (statusData.status === 'generating') setTimeout(pollStatus, 1000); else setGenerating(false);
      };
      await pollStatus();
    } catch (error) {
      console.error('Report generation failed:', error);
      setRecentReports(prev => prev.map(r => r.id === newReport.id ? { ...r, status: 'failed', progress: 0 } : r));
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/20"><FileText className="h-6 w-6" /></div>
          <div><h1 className="text-xl font-bold text-foreground">Reports</h1><p className="text-sm text-muted-foreground">Generate AI-powered reports</p></div>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" />Choose a Template</h2>
            <span className="text-xs text-muted-foreground">{REPORT_TEMPLATES.length} templates available</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {REPORT_TEMPLATES.map((t) => <TemplateCard key={t.id} template={t} selected={selectedTemplate === t.id} onClick={() => { setSelectedTemplate(t.id); const defaults: Record<string, string> = {}; t.parameters.forEach(p => { if (p.defaultValue) defaults[p.id] = p.defaultValue; }); setParamValues(defaults); }} />)}
          </div>
        </div>
        <div className="space-y-4">
          <div className="p-5 rounded-xl border border-border bg-card">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4"><Settings className="w-4 h-4 text-muted-foreground" />Configuration</h2>
            {template ? (
              <>
                <ParameterForm parameters={template.parameters} values={paramValues} onChange={(id, value) => setParamValues(prev => ({ ...prev, [id]: value }))} />
                <button onClick={handleGenerate} disabled={generating} className={cn("w-full mt-6 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed")}>
                  {generating ? <><Loader2 className="w-5 h-5 animate-spin" />Generating...</> : <><Sparkles className="w-5 h-5" />Generate Report</>}
                </button>
              </>
            ) : (
              <div className="text-center py-8"><FileText className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Select a template to configure</p></div>
            )}
          </div>
          {recentReports.length > 0 && (
            <div className="p-5 rounded-xl border border-border bg-card">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4"><Clock className="w-4 h-4 text-muted-foreground" />Recent Reports</h2>
              <div className="space-y-3">{recentReports.slice(0, 5).map((report) => <GenerationProgress key={report.id} report={report} />)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
